import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Calendar, Clock, User, Mail, Phone, FileText, MapPin, Navigation,
  CheckCircle, AlertCircle, Copy, ClipboardCheck, ChevronRight, Send, 
  MessageSquare, RefreshCw, XCircle, Search
} from 'lucide-react';
import { practiceInfo } from '../data/siteData';
import { appointmentAPI } from '../services/api';
import './AppointmentPage.css';

// ============================================
// Phone formatting helper
// ============================================
function formatPhoneInput(value) {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

// ============================================
// Initial form state
// ============================================
const initialFormState = {
  name: '',
  email: '',
  phone: '',
  patientType: 'new',
  preferredDate: '',
  preferredTime: 'Any Time',
  appointmentType: '',
  message: '',
  confirmationNumber: '',
  newPreferredDate: '',
  newPreferredTime: 'Any Time',
  cancelReason: '',
  lookupCode: ''
};

function AppointmentPage() {
  const [activeTab, setActiveTab] = useState('request');
  const [formData, setFormData] = useState(initialFormState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [agreeToSms, setAgreeToSms] = useState(false);
  const [copied, setCopied] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const [lookupResult, setLookupResult] = useState(null);
    const formRef = useRef(null);

  // Scroll to form when success message is shown
  useEffect(() => {
    if (submitResult && submitResult.type === 'success' && formRef.current) {
      // Wait a bit for the success message to render
      setTimeout(() => {
        formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [submitResult]);


  // ============================================
  // Handlers
  // ============================================
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    if (name === 'phone') {
      setFormData(prev => ({ ...prev, phone: formatPhoneInput(value) }));
    } else if (name === 'confirmationNumber' || name === 'lookupCode') {
      setFormData(prev => ({ ...prev, [name]: value.toUpperCase() }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    if (name === 'message') {
      setCharCount(value.length);
    }
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: '' }));
    }
  }, [fieldErrors]);

  const switchTab = useCallback((tab) => {
    setActiveTab(tab);
    setSubmitResult(null);
    setFieldErrors({});
    setLookupResult(null);
    setFormData(initialFormState);
    setAgreeToSms(false);
    setCharCount(0);
    setCopied(false);
  }, []);

  // ============================================
  // Client-side validation
  // ============================================
  const validateForm = () => {
    const errors = {};
    
    if (activeTab === 'request') {
      // New appointment: validate name, email, phone, date
      if (!formData.name.trim() || formData.name.trim().length < 2) {
        errors.name = 'Please enter your full name';
      }
      if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        errors.email = 'Please enter a valid email address';
      }
      const phoneDigits = formData.phone.replace(/\D/g, '');
      if (phoneDigits.length !== 10) {
        errors.phone = 'Please enter a valid 10-digit phone number';
      }
      if (!formData.preferredDate) {
        errors.preferredDate = 'Please select a preferred date';
      }
    }

    if (activeTab === 'reschedule' || activeTab === 'cancel') {
      // Reschedule/cancel: validate confirmation number
      if (!formData.confirmationNumber.trim()) {
        errors.confirmationNumber = 'Please enter your confirmation number';
      }
    }
    
    if (activeTab === 'reschedule' && !formData.newPreferredDate) {
      errors.newPreferredDate = 'Please select a new preferred date';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ============================================
  // Form submission
  // ============================================
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setSubmitResult(null);

    try {
      let response;

      if (activeTab === 'request') {
        response = await appointmentAPI.createNew({
          name: formData.name.trim(),
          email: formData.email.trim(),
          phone: formData.phone,
          patientType: formData.patientType,
          preferredDate: formData.preferredDate,
          preferredTime: formData.preferredTime,
          appointmentType: formData.appointmentType,
          message: formData.message.trim(),
          agreeToSms
        });
      } else if (activeTab === 'reschedule') {
        response = await appointmentAPI.reschedule({
          confirmationNumber: formData.confirmationNumber.trim(),
          newPreferredDate: formData.newPreferredDate,
          newPreferredTime: formData.newPreferredTime,
          message: formData.message.trim()
        });
      } else if (activeTab === 'cancel') {
        response = await appointmentAPI.cancel({
          confirmationNumber: formData.confirmationNumber.trim(),
          cancelReason: formData.cancelReason,
          message: formData.message.trim()
        });
      }

      setSubmitResult({
        type: 'success',
        message: response.message,
        data: response.data
      });
    } catch (err) {
      const errorMsg = err.errors
        ? (Array.isArray(err.errors) && typeof err.errors[0] === 'object'
            ? err.errors.map(e => e.message).join('. ')
            : err.errors.join('. '))
        : 'Something went wrong. Please try again or call us at (215) 676-0222.';

      setSubmitResult({ type: 'error', message: errorMsg });
    } finally {
      setIsSubmitting(false);
    }
  };

  // ============================================
  // Appointment lookup
  // ============================================
  const handleLookup = async (e) => {
    e.preventDefault();
    if (!formData.lookupCode.trim()) {
      setFieldErrors({ lookupCode: 'Please enter a confirmation number' });
      return;
    }

    setIsSubmitting(true);
    setLookupResult(null);

    try {
      const response = await appointmentAPI.lookup(formData.lookupCode.trim());
      setLookupResult({ type: 'success', data: response.data });
    } catch (err) {
      setLookupResult({
        type: 'error',
        message: err.errors?.[0] || 'Could not find that appointment. Please check the confirmation number.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData(initialFormState);
    setSubmitResult(null);
    setFieldErrors({});
    setAgreeToSms(false);
    setCharCount(0);
    setCopied(false);
  };

  const copyConfirmation = (code) => {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const renderFieldError = (field) => {
    if (!fieldErrors[field]) return null;
    return <span className="field-error">{fieldErrors[field]}</span>;
  };

  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'Pending Review', className: 'status-pending' },
      confirmed: { label: 'Confirmed', className: 'status-confirmed' },
      rescheduled: { label: 'Rescheduled', className: 'status-rescheduled' },
      cancelled: { label: 'Cancelled', className: 'status-cancelled' },
      completed: { label: 'Completed', className: 'status-completed' },
      no_show: { label: 'No Show', className: 'status-noshow' }
    };
    const config = statusConfig[status] || { label: status, className: '' };
    return <span className={`status-badge ${config.className}`}>{config.label}</span>;
  };

  // ============================================
  // Render Success Message
  // ============================================
  const renderSuccess = () => (
    <div className="success-message">
      <div className="success-icon-wrapper">
        <CheckCircle size={52} />
      </div>
      <h3>
        {activeTab === 'request' && 'Appointment Request Submitted!'}
        {activeTab === 'reschedule' && 'Reschedule Request Submitted!'}
        {activeTab === 'cancel' && 'Cancellation Submitted!'}
      </h3>
      <p>{submitResult.message}</p>

      {submitResult.data?.confirmationNumber && (
        <div className="confirmation-box">
          <span className="confirmation-label">Your Confirmation Number</span>
          <div className="confirmation-number-row">
            <span className="confirmation-code">
              {submitResult.data.confirmationNumber}
            </span>
            <button
              type="button"
              className="copy-btn"
              onClick={() => copyConfirmation(submitResult.data.confirmationNumber)}
              title="Copy to clipboard"
            >
              {copied ? <ClipboardCheck size={18} /> : <Copy size={18} />}
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
          <span className="confirmation-hint">
            Save this number for your records
          </span>
        </div>
      )}

      <button className="btn btn-primary" onClick={resetForm}>
        {activeTab === 'request' && 'Request Another Appointment'}
        {activeTab === 'reschedule' && 'Submit Another Request'}
        {activeTab === 'cancel' && 'Go Back'}
      </button>
    </div>
  );

  return (
    <main className="appointment-page">
      {/* Hero */}
      <div className="page-hero">
        <div className="page-hero-overlay"></div>
        <div className="container">
          <nav className="breadcrumb">
            <Link to="/">Home</Link>
            <ChevronRight size={14} />
            <span>Appointment Request</span>
          </nav>
          <h1>Appointment Management</h1>
          <p className="page-subtitle">Request, reschedule, cancel, or look up your appointment</p>
        </div>
      </div>

      {/* Main Content */}
      <div className="appointment-content">
        <div className="container">
          <div className="appointment-grid">
            {/* Left Column — Info & Map */}
            <div className="appointment-info">
              <h2>Schedule Your Visit</h2>
              <p>
                The first step towards a beautiful, healthy smile is to schedule an appointment. 
                Please contact our office by phone or use the form to manage your appointments.
              </p>

              <div className="info-cards">
                <div className="info-card">
                  <div className="info-icon"><MapPin size={24} /></div>
                  <div className="info-content">
                    <h3>Our Location</h3>
                    <p>
                      {practiceInfo.address.street}<br />
                      {practiceInfo.address.city}, {practiceInfo.address.state} {practiceInfo.address.zip}
                    </p>
                  </div>
                </div>

                <div className="info-card">
                  <div className="info-icon"><Phone size={24} /></div>
                  <div className="info-content">
                    <h3>Phone</h3>
                    <a href={`tel:${practiceInfo.phone.replace(/[^0-9]/g, '')}`}>
                      {practiceInfo.phone}
                    </a>
                    <p className="info-note">For immediate assistance</p>
                  </div>
                </div>

                <div className="info-card">
                  <div className="info-icon"><Mail size={24} /></div>
                  <div className="info-content">
                    <h3>Email</h3>
                    <a href={`mailto:${practiceInfo.email}`}>{practiceInfo.email}</a>
                  </div>
                </div>

                <div className="info-card">
                  <div className="info-icon"><Clock size={24} /></div>
                  <div className="info-content">
                    <h3>Office Hours</h3>
                    <p>
                      Mon-Tue-Thu: 9am - 6pm<br />
                      Wed: 9am - 5pm<br />
                      Fri: 9am - 3pm<br />
                      Sat: 9am - 2pm
                    </p>
                  </div>
                </div>
              </div>

              {/* Emergency Notice */}
              <div className="emergency-notice">
                <h3><AlertCircle size={20} /> Dental Emergency?</h3>
                <p>
                  If you have a dental emergency, please call our office immediately at{' '}
                  <a href={`tel:${practiceInfo.phone.replace(/[^0-9]/g, '')}`}>
                    {practiceInfo.phone}
                  </a>
                  . We make every effort to see emergency patients as quickly as possible.
                </p>
              </div>

              {/* Google Map */}
              <div className="contact-map">
                <h3><Navigation size={18} /> Find Us</h3>
                <div className="map-wrapper">
                  <iframe
                    title="P3 Dental Location"
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3046.2!2d-75.0155!3d40.1022!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c6b4e8f6b4b8f7%3A0x1234567890abcdef!2s10000+E+Roosevelt+Blvd%2C+Philadelphia%2C+PA+19116!5e0!3m2!1sen!2sus!4v1700000000000!5m2!1sen!2sus"
                    width="100%"
                    height="250"
                    style={{ border: 0, borderRadius: 'var(--radius-md)' }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  ></iframe>
                </div>
                <a
                  href="https://www.google.com/maps/dir/?api=1&destination=10000+E+Roosevelt+Blvd+Philadelphia+PA+19116"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="directions-link"
                >
                  <MapPin size={16} /> Get Directions
                </a>
              </div>
            </div>

            {/* Right Column — Forms */}
            <div className="appointment-form-wrapper" ref={formRef}>
              {/* Tab Navigation */}
              <div className="form-tabs">
                <button
                  className={`tab-btn ${activeTab === 'request' ? 'active' : ''}`}
                  onClick={() => switchTab('request')}
                >
                  <Calendar size={18} />
                  New Appointment
                </button>
                <button
                  className={`tab-btn ${activeTab === 'reschedule' ? 'active' : ''}`}
                  onClick={() => switchTab('reschedule')}
                >
                  <RefreshCw size={18} />
                  Reschedule
                </button>
                <button
                  className={`tab-btn ${activeTab === 'cancel' ? 'active' : ''}`}
                  onClick={() => switchTab('cancel')}
                >
                  <XCircle size={18} />
                  Cancel
                </button>
                <button
                  className={`tab-btn ${activeTab === 'lookup' ? 'active' : ''}`}
                  onClick={() => switchTab('lookup')}
                >
                  <Search size={18} />
                  Look Up
                </button>
              </div>

              {/* Show success screen after submit */}
              {submitResult?.type === 'success' ? renderSuccess() : (
                <>
                  {submitResult?.type === 'error' && (
                    <div className="error-alert">
                      <AlertCircle size={20} />
                      <span>{submitResult.message}</span>
                    </div>
                  )}

                  {/* REQUEST TAB */}
                  {activeTab === 'request' && (
                    <form className="appointment-form" onSubmit={handleSubmit} noValidate>
                      <div className="form-header">
                        <h3>Request New Appointment</h3>
                        <p>Fill out the form below and we'll contact you to confirm.</p>
                      </div>

                      {/* Patient Type */}
                      <div className="form-group patient-type-group">
                        <label className="patient-type-label">
                          <User size={16} /> Patient Type <span className="required">*</span>
                        </label>
                        <div className="patient-type-options">
                          <label className={`patient-type-card ${formData.patientType === 'new' ? 'selected' : ''}`}>
                            <input
                              type="radio"
                              name="patientType"
                              value="new"
                              checked={formData.patientType === 'new'}
                              onChange={handleChange}
                            />
                            <div className="patient-type-content">
                              <h4>New Patient</h4>
                              <p>First time visiting P3 Dental</p>
                            </div>
                          </label>
                          <label className={`patient-type-card ${formData.patientType === 'existing' ? 'selected' : ''}`}>
                            <input
                              type="radio"
                              name="patientType"
                              value="existing"
                              checked={formData.patientType === 'existing'}
                              onChange={handleChange}
                            />
                            <div className="patient-type-content">
                              <h4>Existing Patient</h4>
                              <p>Already a patient at P3 Dental</p>
                            </div>
                          </label>
                        </div>
                      </div>

                      {/* Name */}
                      <div className={`form-group ${fieldErrors.name ? 'has-error' : ''}`}>
                        <label htmlFor="req-name">
                          <User size={16} /> Full Name <span className="required">*</span>
                        </label>
                        <input
                          type="text"
                          id="req-name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="Enter your full name"
                        />
                        {renderFieldError('name')}
                      </div>

                      {/* Email & Phone */}
                      <div className="form-row">
                        <div className={`form-group ${fieldErrors.email ? 'has-error' : ''}`}>
                          <label htmlFor="req-email">
                            <Mail size={16} /> Email <span className="required">*</span>
                          </label>
                          <input
                            type="email"
                            id="req-email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="your@email.com"
                          />
                          {renderFieldError('email')}
                        </div>
                        <div className={`form-group ${fieldErrors.phone ? 'has-error' : ''}`}>
                          <label htmlFor="req-phone">
                            <Phone size={16} /> Phone <span className="required">*</span>
                          </label>
                          <input
                            type="tel"
                            id="req-phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="(215) 555-0000"
                          />
                          {renderFieldError('phone')}
                        </div>
                      </div>

                      {/* Date & Time */}
                      <div className="form-row">
                        <div className={`form-group ${fieldErrors.preferredDate ? 'has-error' : ''}`}>
                          <label htmlFor="req-date">
                            <Calendar size={16} /> Preferred Date <span className="required">*</span>
                          </label>
                          <input
                            type="date"
                            id="req-date"
                            name="preferredDate"
                            value={formData.preferredDate}
                            onChange={handleChange}
                            min={getMinDate()}
                          />
                          {renderFieldError('preferredDate')}
                        </div>
                        <div className="form-group">
                          <label htmlFor="req-time">
                            <Clock size={16} /> Preferred Time
                          </label>
                          <select
                            id="req-time"
                            name="preferredTime"
                            value={formData.preferredTime}
                            onChange={handleChange}
                          >
                            <option value="Any Time">Any Time</option>
                            <option value="Morning (9am-12pm)">Morning (9am-12pm)</option>
                            <option value="Afternoon (12pm-3pm)">Afternoon (12pm-3pm)</option>
                            <option value="Evening (3pm-6pm)">Evening (3pm-6pm)</option>
                          </select>
                        </div>
                      </div>

                      {/* Appointment Type */}
                      <div className="form-group">
                        <label htmlFor="req-type">
                          <FileText size={16} /> Appointment Type
                        </label>
                        <select
                          id="req-type"
                          name="appointmentType"
                          value={formData.appointmentType}
                          onChange={handleChange}
                        >
                          <option value="">Select type (optional)</option>
                          <option value="Cleaning & Checkup">Cleaning & Checkup</option>
                          <option value="New Patient Exam">New Patient Exam</option>
                          <option value="Dental Emergency">Dental Emergency</option>
                          <option value="Cosmetic Consultation">Cosmetic Consultation</option>
                          <option value="Invisalign Consultation">Invisalign Consultation</option>
                          <option value="Dental Implants">Dental Implants</option>
                          <option value="Root Canal">Root Canal</option>
                          <option value="Crown or Bridge">Crown or Bridge</option>
                          <option value="Teeth Whitening">Teeth Whitening</option>
                          <option value="Orthodontics">Orthodontics</option>
                          <option value="Periodontal Treatment">Periodontal Treatment</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>

                      {/* Message */}
                      <div className="form-group">
                        <label htmlFor="req-message">
                          <MessageSquare size={16} /> Additional Notes
                        </label>
                        <textarea
                          id="req-message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          rows="4"
                          placeholder="Any additional information or special requests (optional)"
                          maxLength={1000}
                        ></textarea>
                        <div className="textarea-footer">
                          <span className="char-count">{charCount}/1000</span>
                        </div>
                      </div>

                      {/* SMS Consent */}
                      <div className="form-group checkbox-group">
                        <label className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={agreeToSms}
                            onChange={(e) => setAgreeToSms(e.target.checked)}
                          />
                          <span className="checkbox-text">
                            I consent to receive SMS/text messages for appointment reminders. 
                            Msg and data rates may apply. Reply STOP to cancel.
                          </span>
                        </label>
                      </div>

                      {/* Note */}
                      <div className="form-note">
                        <p>
                          <strong>Note:</strong> Our staff will contact you within 24 hours to 
                          confirm your appointment time.
                        </p>
                      </div>

                      {/* Submit */}
                      <button
                        type="submit"
                        className="btn btn-primary btn-submit"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? 'Submitting...' : (
                          <>
                            <Send size={18} /> Request Appointment
                          </>
                        )}
                      </button>
                    </form>
                  )}

                  {/* RESCHEDULE TAB */}
                  {activeTab === 'reschedule' && (
                    <form className="appointment-form" onSubmit={handleSubmit} noValidate>
                      <div className="form-header">
                        <h3>Reschedule Appointment</h3>
                        <p>Enter your confirmation number to reschedule your existing appointment.</p>
                      </div>

                      {/* Confirmation Number */}
                      <div className={`form-group ${fieldErrors.confirmationNumber ? 'has-error' : ''}`}>
                        <label htmlFor="resc-confirmation">
                          <Search size={16} /> Confirmation Number <span className="required">*</span>
                        </label>
                        <input
                          type="text"
                          id="resc-confirmation"
                          name="confirmationNumber"
                          value={formData.confirmationNumber}
                          onChange={handleChange}
                          placeholder="e.g. P3D-7HZ4DD"
                        />
                        {renderFieldError('confirmationNumber')}
                      </div>

                      {/* New Date & Time */}
                      <div className="form-row">
                        <div className={`form-group ${fieldErrors.newPreferredDate ? 'has-error' : ''}`}>
                          <label htmlFor="resc-new-date">
                            <Calendar size={16} /> New Preferred Date <span className="required">*</span>
                          </label>
                          <input
                            type="date"
                            id="resc-new-date"
                            name="newPreferredDate"
                            value={formData.newPreferredDate}
                            onChange={handleChange}
                            min={getMinDate()}
                          />
                          {renderFieldError('newPreferredDate')}
                        </div>
                        <div className="form-group">
                          <label htmlFor="resc-new-time">
                            <Clock size={16} /> New Preferred Time
                          </label>
                          <select
                            id="resc-new-time"
                            name="newPreferredTime"
                            value={formData.newPreferredTime}
                            onChange={handleChange}
                          >
                            <option value="Any Time">Any Time</option>
                            <option value="Morning (9am-12pm)">Morning (9am-12pm)</option>
                            <option value="Afternoon (12pm-3pm)">Afternoon (12pm-3pm)</option>
                            <option value="Evening (3pm-6pm)">Evening (3pm-6pm)</option>
                          </select>
                        </div>
                      </div>

                      {/* Message */}
                      <div className="form-group">
                        <label htmlFor="resc-message">
                          <MessageSquare size={16} /> Reason for Rescheduling (Optional)
                        </label>
                        <textarea
                          id="resc-message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          rows="3"
                          placeholder="Optional: Let us know why you need to reschedule"
                          maxLength={500}
                        ></textarea>
                        <div className="textarea-footer">
                          <span className="char-count">{charCount}/500</span>
                        </div>
                      </div>

                      {/* Note */}
                      <div className="form-note">
                        <p>
                          <strong>Note:</strong> We'll contact you to confirm your new appointment time.
                        </p>
                      </div>

                      {/* Submit */}
                      <button
                        type="submit"
                        className="btn btn-primary btn-submit"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? 'Submitting...' : (
                          <>
                            <RefreshCw size={18} /> Submit Reschedule Request
                          </>
                        )}
                      </button>
                    </form>
                  )}

                  {/* CANCEL TAB */}
                  {activeTab === 'cancel' && (
                    <form className="appointment-form" onSubmit={handleSubmit} noValidate>
                      <div className="form-header">
                        <h3>Cancel Appointment</h3>
                        <p>Enter your confirmation number to cancel your appointment.</p>
                      </div>

                      {/* Confirmation Number */}
                      <div className={`form-group ${fieldErrors.confirmationNumber ? 'has-error' : ''}`}>
                        <label htmlFor="canc-confirmation">
                          <Search size={16} /> Confirmation Number <span className="required">*</span>
                        </label>
                        <input
                          type="text"
                          id="canc-confirmation"
                          name="confirmationNumber"
                          value={formData.confirmationNumber}
                          onChange={handleChange}
                          placeholder="e.g. P3D-7HZ4DD"
                        />
                        {renderFieldError('confirmationNumber')}
                      </div>

                      {/* Cancel Reason */}
                      <div className="form-group">
                        <label htmlFor="canc-reason">
                          <FileText size={16} /> Reason for Cancellation
                        </label>
                        <select
                          id="canc-reason"
                          name="cancelReason"
                          value={formData.cancelReason}
                          onChange={handleChange}
                        >
                          <option value="">Select reason (optional)</option>
                          <option value="Schedule Conflict">Schedule Conflict</option>
                          <option value="Found Another Provider">Found Another Provider</option>
                          <option value="Financial Reasons">Financial Reasons</option>
                          <option value="No Longer Needed">No Longer Needed</option>
                          <option value="Emergency">Emergency</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>

                      {/* Message */}
                      <div className="form-group">
                        <label htmlFor="canc-message">
                          <MessageSquare size={16} /> Additional Comments (Optional)
                        </label>
                        <textarea
                          id="canc-message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          rows="3"
                          placeholder="Optional: Any additional comments"
                          maxLength={500}
                        ></textarea>
                        <div className="textarea-footer">
                          <span className="char-count">{charCount}/500</span>
                        </div>
                      </div>

                      {/* Note */}
                      <div className="form-note">
                        <p>
                          <strong>Note:</strong> Please cancel at least 24 hours in advance to avoid 
                          cancellation fees.
                        </p>
                      </div>

                      {/* Submit */}
                      <button
                        type="submit"
                        className="btn btn-primary btn-submit"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? 'Submitting...' : (
                          <>
                            <XCircle size={18} /> Cancel Appointment
                          </>
                        )}
                      </button>
                    </form>
                  )}

                  {/* LOOKUP TAB */}
                  {activeTab === 'lookup' && (
                    <>
                      <div className="form-header">
                        <h3>Look Up Appointment</h3>
                        <p>Enter your confirmation number to check appointment status.</p>
                      </div>

                      <form className="appointment-form" onSubmit={handleLookup} noValidate>
                        <div className={`form-group ${fieldErrors.lookupCode ? 'has-error' : ''}`}>
                          <label htmlFor="lookup-code">
                            <Search size={16} /> Confirmation Number <span className="required">*</span>
                          </label>
                          <input
                            type="text"
                            id="lookup-code"
                            name="lookupCode"
                            value={formData.lookupCode}
                            onChange={handleChange}
                            placeholder="Enter your confirmation number"
                          />
                          {renderFieldError('lookupCode')}
                        </div>

                        <button
                          type="submit"
                          className="btn btn-primary btn-submit"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? 'Looking up...' : (
                            <>
                              <Search size={18} /> Look Up Appointment
                            </>
                          )}
                        </button>
                      </form>

                      {/* Lookup Result */}
                      {lookupResult?.type === 'error' && (
                        <div className="error-alert" style={{ marginTop: '20px' }}>
                          <AlertCircle size={20} />
                          <span>{lookupResult.message}</span>
                        </div>
                      )}

                      {lookupResult?.type === 'success' && lookupResult.data && (
                        <div className="lookup-result">
                          <h4>Appointment Details</h4>
                          <div className="lookup-details">
                            <div className="lookup-item">
                              <span className="lookup-label">Confirmation #:</span>
                              <span>{lookupResult.data.confirmation_number}</span>
                            </div>
                            <div className="lookup-item">
                              <span className="lookup-label">Status:</span>
                              <span>{getStatusBadge(lookupResult.data.status)}</span>
                            </div>
                            <div className="lookup-item">
                              <span className="lookup-label">Name:</span>
                              <span>{lookupResult.data.patient_name}</span>
                            </div>
                            <div className="lookup-item">
                              <span className="lookup-label">Email:</span>
                              <span>{lookupResult.data.patient_email}</span>
                            </div>
                            {lookupResult.data.patient_phone && (
                              <div className="lookup-item">
                                <span className="lookup-label">Phone:</span>
                                <span>{lookupResult.data.patient_phone}</span>
                              </div>
                            )}
                            {lookupResult.data.appointment_type && (
                              <div className="lookup-item">
                                <span className="lookup-label">Type:</span>
                                <span>{lookupResult.data.appointment_type}</span>
                              </div>
                            )}
                            {lookupResult.data.preferred_date && (
                              <div className="lookup-item">
                                <span className="lookup-label">Preferred Date:</span>
                                <span>{new Date(lookupResult.data.preferred_date).toLocaleDateString()}</span>
                              </div>
                            )}
                            {lookupResult.data.preferred_time && (
                              <div className="lookup-item">
                                <span className="lookup-label">Preferred Time:</span>
                                <span>{lookupResult.data.preferred_time}</span>
                              </div>
                            )}
                            {lookupResult.data.confirmed_date && (
                              <div className="lookup-item">
                                <span className="lookup-label">Confirmed Date:</span>
                                <span>{new Date(lookupResult.data.confirmed_date).toLocaleDateString()}</span>
                              </div>
                            )}
                            {lookupResult.data.confirmed_time && (
                              <div className="lookup-item">
                                <span className="lookup-label">Confirmed Time:</span>
                                <span>{lookupResult.data.confirmed_time}</span>
                              </div>
                            )}
                            {lookupResult.data.cancel_reason && (
                              <div className="lookup-item">
                                <span className="lookup-label">Cancel Reason:</span>
                                <span>{lookupResult.data.cancel_reason}</span>
                              </div>
                            )}
                            <div className="lookup-item">
                              <span className="lookup-label">Submitted:</span>
                              <span>{new Date(lookupResult.data.created_at).toLocaleString()}</span>
                            </div>
                            {lookupResult.data.updated_at !== lookupResult.data.created_at && (
                              <div className="lookup-item">
                                <span className="lookup-label">Last Updated:</span>
                                <span>{new Date(lookupResult.data.updated_at).toLocaleString()}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}

export default AppointmentPage;
